/**
 * Enhanced API utilities for handling requests, responses, and errors
 * Now supports cookie-based authentication and enterprise auth integration
 * Added retry logic and improved error handling for Patient Portal QA
 */
import axios from 'axios';
import apiConfig from './apiConfig';

/**
 * Determines if the application is running locally or in production
 * @returns {boolean} True if running locally, false if in production
 */
export const isRunningLocally = () => {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

/**
 * Creates a standard API request configuration with enterprise auth support
 * @returns {Object} Axios request configuration
 */
export const createApiRequestConfig = () => {
  // Get access token from localStorage (for cross-tab persistence) - check both possible keys
  const accessToken =
    localStorage.getItem('_at') ||
    localStorage.getItem('token') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('patientToken'); // Support patient portal tokens

  return {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    // Enable credentials for cookie-based authentication
    withCredentials: true,
  };
};

const isAbsoluteUrl = value => /^https?:\/\//i.test(value || '');

const normalizeApiEndpoint = endpoint => {
  if (endpoint === null || endpoint === undefined) return '';
  if (typeof endpoint !== 'string') return String(endpoint);
  const trimmed = endpoint.trim();
  if (!trimmed) return '';
  if (isAbsoluteUrl(trimmed)) return trimmed;
  const withoutLeadingSlash = trimmed.replace(/^\/+/, '');
  return withoutLeadingSlash.startsWith('api/') ? withoutLeadingSlash.slice(4) : withoutLeadingSlash;
};

/**
 * Returns the appropriate API URL based on the environment and endpoint
 * @param {string} endpoint - The API endpoint
 * @param {string} cloudflareWorkerUrl - The Cloudflare Worker URL
 * @returns {string} The complete API URL
 */
export const getApiUrl = (endpoint, cloudflareWorkerUrl = '') => {
  const isLocal = isRunningLocally();
  const normalized = normalizeApiEndpoint(endpoint);

  if (isAbsoluteUrl(normalized)) {
    return normalized;
  }

  const apiPath = normalized ? `/api/${normalized}` : '/api';
  if (isLocal) {
    // Local development - use relative URL
    return apiPath;
  }

  // Production - use proper base URL if cloudflareWorkerUrl is not provided
  const baseUrl = cloudflareWorkerUrl || `${window.location.protocol}//${window.location.host}`;
  return `${baseUrl}${apiPath}`;
};

/**
 * Makes an API GET request with proper error handling
 * @param {string} url - The API URL
 * @param {Function} fallbackFn - Function to provide fallback data on error
 * @returns {Promise<any>} The API response data or fallback data
 */
export const apiGet = async (url, fallbackFn) => {
  try {
    const response = await axios.get(url, createApiRequestConfig());

    // Check if response.data exists and is valid
    if (response && response.data) {
      return response.data;
    }

    console.warn(`[API] Invalid response from ${url}, using fallback data`);
    return fallbackFn ? fallbackFn() : null;
  } catch (error) {
    console.error(`[API] Error fetching from ${url}:`, error);

    // In production, use fallback data
    if (!isRunningLocally()) {
      console.warn(`[API] Using fallback data for ${url} due to error`);
      return fallbackFn ? fallbackFn() : null;
    }

    // In development, throw the error
    throw error;
  }
};

/**
 * Makes an API POST request with proper error handling
 * @param {string} url - The API URL
 * @param {any} data - The data to post
 * @param {Function} fallbackFn - Function to provide fallback data on error
 * @returns {Promise<any>} The API response data or fallback data
 */
export const apiPost = async (url, data, fallbackFn) => {
  try {
    const response = await axios.post(url, data, createApiRequestConfig());

    // Check if response.data exists and is valid
    if (response && response.data) {
      return response.data;
    }

    console.warn(`[API] Invalid response from ${url}, using fallback data`);
    return fallbackFn ? fallbackFn() : null;
  } catch (error) {
    console.error(`[API] Error posting to ${url}:`, error);

    // In production, use fallback data
    if (!isRunningLocally()) {
      console.warn(`[API] Using fallback data for ${url} due to error`);
      return fallbackFn ? fallbackFn() : null;
    }

    // In development, throw the error
    throw error;
  }
};

/**
 * Makes an API PUT request with proper error handling
 * @param {string} url - The API URL
 * @param {any} data - The data to put
 * @param {Function} fallbackFn - Function to provide fallback data on error
 * @returns {Promise<any>} The API response data or fallback data
 */
export const apiPut = async (url, data, fallbackFn) => {
  try {
    const response = await axios.put(url, data, createApiRequestConfig());

    // Check if response.data exists and is valid
    if (response && response.data) {
      return response.data;
    }

    console.warn(`[API] Invalid response from ${url}, using fallback data`);
    return fallbackFn ? fallbackFn() : null;
  } catch (error) {
    console.error(`[API] Error putting to ${url}:`, error);

    // In production, use fallback data
    if (!isRunningLocally()) {
      console.warn(`[API] Using fallback data for ${url} due to error`);
      return fallbackFn ? fallbackFn() : null;
    }

    // In development, throw the error
    throw error;
  }
};

/**
 * Makes an API DELETE request with proper error handling
 * @param {string} url - The API URL
 * @param {Function} fallbackFn - Function to provide fallback data on error
 * @returns {Promise<any>} The API response data or fallback data
 */
export const apiDelete = async (url, fallbackFn) => {
  try {
    const response = await axios.delete(url, createApiRequestConfig());

    // Check if response.data exists and is valid
    if (response && response.data) {
      return response.data;
    }

    console.warn(`[API] Invalid response from ${url}, using fallback data`);
    return fallbackFn ? fallbackFn() : null;
  } catch (error) {
    console.error(`[API] Error deleting from ${url}:`, error);

    // In production, use fallback data
    if (!isRunningLocally()) {
      console.warn(`[API] Using fallback data for ${url} due to error`);
      return fallbackFn ? fallbackFn() : null;
    }

    // In development, throw the error
    throw error;
  }
};

/**
 * Enhanced API utilities class for enterprise authentication
 */
class ApiUtils {
  constructor() {
    this.baseURL = this.getBaseURL();
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
    });
    this.setupAxiosInterceptors();
    // Retry configuration
    this.maxRetries = 3;
    this.retryDelay = 1000; // Initial delay in ms
  }

  getBaseURL() {
    if (isRunningLocally()) {
      // Use same-origin so '/api/*' goes through Vite proxy to the worker
      return '';
    }
    // In production, frontend and API share the same domain; use relative base
    return '';
  }

  /**
   * Performs an operation with exponential backoff retry logic
   * @param {Function} operation - The async operation to perform
   * @param {number} retries - Number of retries remaining
   * @param {number} delay - Current delay before retry
   * @returns {Promise<any>} The operation result
   */
  async withRetry(operation, retries = this.maxRetries, delay = this.retryDelay) {
    try {
      return await operation();
    } catch (error) {
      // Don't retry on certain error codes
      const nonRetryableStatuses = [400, 401, 403, 404, 422];
      if (error.response && nonRetryableStatuses.includes(error.response.status)) {
        throw error;
      }

      if (retries > 0) {
        console.warn(`[ApiUtils] Request failed, retrying in ${delay}ms... (${retries} retries left)`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.withRetry(operation, retries - 1, delay * 2); // Exponential backoff
      }
      
      throw error;
    }
  }

  setupAxiosInterceptors() {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      config => {
        // Always include credentials for cookie-based auth
        config.withCredentials = true;

        // Add access token if available - check both possible storage keys
        const accessToken =
          localStorage.getItem('_at') ||
          localStorage.getItem('token') ||
          localStorage.getItem('authToken') ||
          localStorage.getItem('patientToken');
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        } else {
          // Only warn about missing auth token for protected endpoints
          const publicEndpoints = [
            '/api/auth/login',
            '/api/asc/auth/login',
            '/api/patient/auth/login',
            '/api/patient/auth/register',
            '/api/patient/auth/forgot-password',
            '/api/patient/auth/me', // Patient auth check endpoint
            '/api/health',
            '/api/auth/me', // Auth check endpoint - expected to be called without token
            '/api/preop-protocols', // May be called on page load
            '/api/protocol-engine/protocols', // May be called on page load
            '/api/protocol-compliance/analytics', // May be called on page load
          ];
          const isPublicEndpoint = publicEndpoints.some(endpoint => config.url.includes(endpoint));
          if (!isPublicEndpoint) {
            console.warn(`[ApiUtils] No auth token found for request to ${config.url}`);
          }
        }

        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for handling auth errors
    this.axiosInstance.interceptors.response.use(
      response => {
        return response;
      },
      async error => {
        if (error.response?.status === 401) {
          // Don't trigger unauthorized event on homepage, public routes, or auth check endpoints
          const currentPath = window.location.pathname;
          const publicPaths = ['/', '/login', '/asc/login', '/oauth/callback'];
          const isPublicPath = publicPaths.some(
            path => currentPath === path || currentPath.startsWith('/oauth/callback')
          );

          // Also check if this is an auth status check
          const isAuthCheck = error.config?.url?.includes('/auth/me');

          // Grace period after successful login to avoid loops while state hydrates
          let withinGrace = false;
          try {
            const justLoggedIn = sessionStorage.getItem('justLoggedIn');
            if (justLoggedIn) {
              const t = parseInt(justLoggedIn, 10);
              if (!Number.isNaN(t) && Date.now() - t < 10000) {
                withinGrace = true;
              }
            }
          } catch {}

          // Attempt a one-time silent refresh + retry before logging out
          const originalConfig = error.config || {};
          const alreadyRetried = originalConfig.__retriedAfter401 === true;
          const eligibleForRefresh = !isPublicPath && !isAuthCheck && !withinGrace && !alreadyRetried;

          if (eligibleForRefresh) {
            try {
              // Mark to avoid loops
              originalConfig.__retriedAfter401 = true;
              // Try refresh
              const refreshResp = await this.axiosInstance.post('/api/auth/refresh', {}, createApiRequestConfig()).catch(() => null);
              if (refreshResp && (refreshResp.status === 200 || refreshResp.data?.success)) {
                // Persist new token if provided in headers or body
                const hdrToken = refreshResp.headers?.['x-new-access-token'] || refreshResp.headers?.['X-New-Access-Token'];
                const bodyToken = refreshResp.data?.data?.token || refreshResp.data?.token;
                const newToken = hdrToken || bodyToken;
                if (newToken) {
                  try {
                    localStorage.setItem('_at', newToken);
                    localStorage.setItem('token', newToken);
                  } catch {}
                }
                // Retry original request with updated auth
                return this.axiosInstance.request(originalConfig);
              }
            } catch (refreshErr) {
              // fall through to unauthorized flow
            }
          }

          if (!isPublicPath && !isAuthCheck && !withinGrace) {
            // Unauthorized after refresh attempt or not eligible -> trigger logout
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async get(endpoint, options = {}) {
    // Determine if this endpoint should use retry logic
    const shouldRetry = endpoint.includes('/questionnaires') || 
                       endpoint.includes('/documents') ||
                       endpoint.includes('/messages') ||
                       endpoint.includes('/patient/');
    
    const operation = async () => {
      // Don't log requests to endpoints that might not exist
      const isOptionalEndpoint = endpoint.includes('/api/ai/enhanced/insights/') || 
                                 endpoint.includes('/api/patients/') && endpoint.includes('/orders');
      
      const response = await this.axiosInstance.get(endpoint, {
        ...createApiRequestConfig(),
        ...options,
      });

      return {
        success: true,
        data: response.data,
        headers: response.headers,
      };
    };

    try {
      if (shouldRetry) {
        return await this.withRetry(operation);
      } else {
        return await operation();
      }
    } catch (error) {
      // Only log non-401 and non-404 errors for certain endpoints
      const is404 = error.response?.status === 404;
      const isExpected404 = is404 && (
        endpoint.includes('/api/ai/enhanced/insights/') ||
        (endpoint.includes('/api/patients/') && endpoint.includes('/orders')) ||
        endpoint.includes('/api/ai/recommendations') ||
        endpoint.includes('/api/clearance/') ||
        endpoint.includes('/api/users/mfa-status')
      );
      
      if (error.response?.status !== 401 && !isExpected404) {
        console.error(`[ApiUtils] GET error for ${endpoint}:`, error);
      }
      return this.handleError(error);
    }
  }

  async post(endpoint, data = {}, options = {}) {
    // Determine if this endpoint should use retry logic
    const shouldRetry = endpoint.includes('/questionnaires') || 
                       endpoint.includes('/documents') ||
                       endpoint.includes('/messages') ||
                       endpoint.includes('/patient/');
    
    const operation = async () => {
      const response = await this.axiosInstance.post(endpoint, data, {
        ...createApiRequestConfig(),
        ...options,
      });

      return {
        success: true,
        data: response.data,
        headers: response.headers,
      };
    };

    try {
      if (shouldRetry) {
        return await this.withRetry(operation);
      } else {
        return await operation();
      }
    } catch (error) {
      return this.handleError(error);
    }
  }

  async put(endpoint, data = {}, options = {}) {
    try {
      const response = await this.axiosInstance.put(endpoint, data, {
        ...createApiRequestConfig(),
        ...options,
      });

      return {
        success: true,
        data: response.data,
        headers: response.headers,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  async delete(endpoint, options = {}) {
    try {
      const response = await this.axiosInstance.delete(endpoint, {
        ...createApiRequestConfig(),
        ...options,
      });

      return {
        success: true,
        data: response.data,
        headers: response.headers,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  async postFormData(endpoint, formData, options = {}) {
    try {
      // Get access token
      const accessToken =
        localStorage.getItem('_at') ||
        localStorage.getItem('token') ||
        localStorage.getItem('authToken');

      const response = await this.axiosInstance.post(endpoint, formData, {
        headers: {
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          // Don't set Content-Type for FormData - browser will set it with boundary
        },
        withCredentials: true,
        ...options,
      });

      return {
        success: true,
        data: response.data,
        headers: response.headers,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  handleError(error) {
    const { response } = error;
    const url = error.config?.url || '';
    
    // Check if this is an expected 404
    const is404 = response?.status === 404;
    const isExpected404 = is404 && (
      url.includes('/api/ai/enhanced/insights/') ||
      (url.includes('/api/patients/') && url.includes('/orders')) ||
      url.includes('/api/ai/recommendations') ||
      url.includes('/api/clearance/') ||
      url.includes('/api/users/mfa-status')
    );

    // Only log non-401 errors and non-expected-404 errors
    if (response?.status !== 401 && !isExpected404) {
      console.error('[ApiUtils] Error details:', {
        message: error.message,
        status: response?.status,
        data: response?.data,
        url: url,
      });
    }

    if (response) {
      return {
        success: false,
        error: response.data?.error || response.data?.message || 'Request failed',
        status: response.status,
        response,
      };
    }

    return {
      success: false,
      error: error.message || 'Network error',
      response: error,
    };
  }
}

// Export singleton instance
export const apiUtils = new ApiUtils();
